import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Tabs from "../../components/Tabs";
import DataTable from "../../components/DataTable";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, listExamResults } from "../../api/examApi";
import "../Attendance/Attendance.css";

const URL_TO_TAB = {
  "exam-report-class-merit-position": "class",
  "exam-report-section-merit-position": "section",
  "exam-report-group-merit-position": "group",
  "exam-report-shift-merit-position": "shift",
  "exam-report-class-failed-list": "classfail",
  "exam-report-section-result": "sectionresult",
};

const TABS = [
  { key: "class", label: "Merit — Class Wise" },
  { key: "section", label: "Merit — Section Wise" },
  { key: "group", label: "Merit — Group Wise" },
  { key: "shift", label: "Merit — Shift Wise" },
  { key: "classfail", label: "Class Fail List" },
  { key: "sectionresult", label: "Section Result (Pass & Fail)" },
];

/** Standard competition ranking (1, 2, 2, 4, ...) by average GPA descending, same
 * algorithm digicampusbd/ExamResultServiceImpl already uses for the class-wide
 * position — re-run here over a scoped subset (one section/group/shift) so each
 * scope gets its own 1..N ranking instead of reusing the class-wide position. */
function rankByGpa(rows) {
  const ranked = [...rows].sort((a, b) => b.avgGpaWithoutOptional - a.avgGpaWithoutOptional);
  let position = 0;
  let previousGpa = null;
  return ranked.map((row, i) => {
    if (row.resultStatus === "Failed") {
      return { ...row, scopedPosition: 0 };
    }
    if (row.avgGpaWithoutOptional !== previousGpa) {
      position = i + 1;
      previousGpa = row.avgGpaWithoutOptional;
    }
    return { ...row, scopedPosition: position };
  });
}

/**
 * Direct port of digicampusbd's 4 "merit position" reports (`ExamReportController`:
 * `/class/merit/position`, `/section/merit/position`, `/group/merit/position`,
 * `/shift/merit/position` — the single most-granted urls found in the Phase 12+
 * "coming soon" audit, 121-129 real non-super-admin grants each). All 4 old
 * endpoints query the exact same underlying `ExamResult` rows for one
 * class+exam (`examConfigId` in this app spans every section/shift/group of
 * that class, same as the old app's `ExamConfiguration`) and differ only in
 * which scope they rank within — so this consolidates them into one page over
 * one existing call (`GET /api/exam-results`, already built for
 * `ExamResultPage`/`exam-result-process-general`) instead of 4 new backend
 * endpoints: `ExamResultResponse` was extended with section/shift/group
 * identifiers (see `ExamMapper`) so the same result set can be filtered and
 * re-ranked per scope entirely client-side. Only "Passed" students are shown,
 * matching every one of the old endpoints' own `resultStatus = 'Passed'` filter.
 * Not ported: the old app's `/update/class/section/group/position` (manually
 * overriding a computed position, e.g. for tie-breaks) — flagged as a
 * deferred, explicit gap, not silently dropped; this page shows the computed
 * ranking only.
 *
 * Also folds in 2 more urls from the same controller, same result set:
 * `exam-report-class-failed-list` (`classFailList` — every failed student,
 * ordered by obtained mark descending, with each one's stored failed-subject
 * *count*) and `exam-report-section-result` (`sectionResults` — every
 * student, pass and fail, in one section, ordered by roll). Not the same as
 * `exam-report-section-failed-list` (`findSectionfailedDetails`), which
 * needs each failed student's actual failed subject *names* via a per-
 * subject-mark lookup — a materially bigger port, deliberately left for a
 * later slice of this same category rather than rushed here.
 */
export default function ExamMeritReportsPage() {
  const { pageUrl } = useParams();
  const [tab, setTab] = useState(URL_TO_TAB[pageUrl] || "class");

  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [sectionId, setSectionId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [shiftId, setShiftId] = useState("");

  useEffect(() => {
    setTab(URL_TO_TAB[pageUrl] || "class");
  }, [pageUrl]);

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError("");
    listExamResults(examConfigId)
      .then(setResults)
      .catch(() => setError("Failed to load results."))
      .finally(() => setLoading(false));
  }, [examConfigId]);

  const passed = useMemo(() => results.filter((r) => r.resultStatus === "Passed"), [results]);

  const sections = useMemo(() => uniqueByIdName(results, "sectionId", "sectionName"), [results]);
  const groups = useMemo(() => uniqueByIdName(results, "groupId", "groupName"), [results]);
  const shifts = useMemo(() => uniqueByIdName(results, "shiftId", "shiftName"), [results]);

  // Re-validate (not just "is something already selected") whenever the
  // class/exam selection changes the available sections/groups/shifts — a
  // previously-selected id can be a non-empty string that no longer exists
  // in the *new* class's list, which a bare truthiness check would miss
  // (found live on a sibling page: switching class then filter left a stale
  // id selected with zero real matches, silently showing no results instead
  // of falling back to a real option).
  useEffect(() => {
    if (sections.length === 0) return;
    if (!sections.some((s) => String(s.id) === sectionId)) setSectionId(String(sections[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);
  useEffect(() => {
    if (groups.length === 0) return;
    if (!groups.some((g) => String(g.id) === groupId)) setGroupId(String(groups[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);
  useEffect(() => {
    if (shifts.length === 0) return;
    if (!shifts.some((s) => String(s.id) === shiftId)) setShiftId(String(shifts[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts]);

  const failed = useMemo(() => results.filter((r) => r.resultStatus === "Failed"), [results]);

  let rows = [];
  const isMeritTab = ["class", "section", "group", "shift"].includes(tab);
  if (tab === "class") {
    rows = passed.slice().sort((a, b) => a.classPosition - b.classPosition).map((r) => ({ ...r, scopedPosition: r.classPosition }));
  } else if (tab === "section") {
    rows = rankByGpa(passed.filter((r) => String(r.sectionId) === sectionId));
  } else if (tab === "group") {
    rows = rankByGpa(passed.filter((r) => String(r.groupId) === groupId));
  } else if (tab === "shift") {
    rows = rankByGpa(passed.filter((r) => String(r.shiftId) === shiftId));
  } else if (tab === "classfail") {
    // Direct port of ExamReportService#classFailList — every failed student for
    // this class+exam, ordered by obtained mark descending (matches the old
    // app's own `OrderByObtainMarkDesc`); no ranking/position shown, same as there.
    rows = failed.slice().sort((a, b) => b.obtainMark - a.obtainMark);
  } else if (tab === "sectionresult") {
    // Direct port of ExamReportService#sectionResults — every student (pass AND
    // fail) in one section, ordered by roll — the "position" column the old
    // app's own response carries here is just the stored class-wide position.
    rows = results.filter((r) => String(r.sectionId) === sectionId).slice().sort((a, b) => a.studentRoll - b.studentRoll);
    rows = rows.map((r) => ({ ...r, scopedPosition: r.classPosition }));
  }
  if (isMeritTab) {
    rows = rows.sort((a, b) => a.scopedPosition - b.scopedPosition || a.studentRoll - b.studentRoll);
  }

  const columns = [
    ...(isMeritTab || tab === "sectionresult"
      ? [{ key: "scopedPosition", label: "Position", render: (row) => (row.scopedPosition === 0 ? "—" : row.scopedPosition) }]
      : []),
    { key: "studentRoll", label: "Roll" },
    { key: "customStudentId", label: "Student ID" },
    { key: "studentName", label: "Student" },
    { key: "sectionName", label: "Section" },
    { key: "shiftName", label: "Shift" },
    { key: "groupName", label: "Group" },
    { key: "obtainMark", label: "Obtained" },
    { key: "totalMark", label: "Total" },
    { key: "letterGrade", label: "Grade" },
    { key: "avgGpaWithoutOptional", label: "GPA" },
    ...(tab === "classfail" ? [{ key: "failedSubject", label: "Failed Subjects" }] : []),
    ...(tab === "sectionresult" ? [{ key: "resultStatus", label: "Status" }] : []),
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Exam Result Reports</h1>
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}

      <div className="attendance-filters">
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>

        {(tab === "section" || tab === "sectionresult") && (
          <label>
            Section
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {tab === "group" && (
          <label>
            Group
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {tab === "shift" && (
          <label>
            Shift
            <select value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading ? <p>Loading...</p> : <DataTable columns={columns} rows={rows} />}
      {!loading && rows.length === 0 && <p>No results found for this selection.</p>}
    </MainLayout>
  );
}

function uniqueByIdName(rows, idKey, nameKey) {
  const seen = new Map();
  rows.forEach((r) => {
    if (r[idKey] != null && !seen.has(r[idKey])) {
      seen.set(r[idKey], { id: r[idKey], name: r[nameKey] });
    }
  });
  return Array.from(seen.values());
}
